import { useState, useEffect, useCallback } from 'react';

/**
 * Спільний стан шапки сторінки «Ринок, ціни» (редизайн).
 *
 * Операційні елементи (вкладка ринку · дата · Графік/Таблиця) винесені в
 * топбар і читаються тілом сторінки — той самий прийом, що useForecastUi /
 * useFinancialResultUi: модульне значення + подія синхронізації в межах вкладки.
 *
 * Діапазон доступних дат (min/max) кладе сюди ТІЛО сторінки після завантаження —
 * топбар бере його, щоб вимикати стрілки ◀/▶ на межах. Дата '' → бекенд сам
 * визначає останню наявну добу для активної вкладки; тіло повертає її назад.
 */
const EV = 'helios:market-prices-ui-change';

// У шапці три вкладки: РДН · ВДР · БР. «БР» — це br_oper або br_fact (перемикач «Опер./Факт.»).
export type MarketTab = 'rdn' | 'idm' | 'br_oper' | 'br_fact';
export type MarketView = 'chart' | 'table';
/** Період плиток і графіка РДН: доба або календарний місяць (лише вкладка «РДН»). */
export type MarketPeriod = 'day' | 'month';

/** Перенесення дати в інший місяць із збереженням дня (31 → останній день короткого місяця), у межах [min, max]. */
export function dateInMonth(iso: string, ym: string, min: string | null, max: string | null): string {
  const day = Number((iso || `${ym}-01`).slice(8, 10)) || 1;
  const [y, m] = ym.split('-').map(Number);
  const last = new Date(Date.UTC(y, m, 0)).getUTCDate();
  let out = `${ym}-${String(Math.min(day, last)).padStart(2, '0')}`;
  if (max && out > max) out = max;
  if (min && out < min) out = min;
  return out;
}

/** Зсув ISO-дати на ±N діб (UTC, без стрибків через літній/зимовий час). */
export function shiftDayIso(iso: string, delta: number): string {
  if (!iso) return iso;
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + delta);
  return `${dt.getUTCFullYear()}-${String(dt.getUTCMonth() + 1).padStart(2, '0')}-${String(dt.getUTCDate()).padStart(2, '0')}`;
}

const state = {
  tab: 'rdn' as MarketTab,
  date: '' as string,          // '' → бекенд резолвить останню наявну добу вкладки
  view: 'chart' as MarketView,
  period: 'day' as MarketPeriod,
  min: null as string | null,  // межі доступних дат (кладе тіло сторінки)
  max: null as string | null,
};

const emit = () => window.dispatchEvent(new CustomEvent(EV));

export function useMarketPricesUi() {
  const [, force] = useState(0);
  useEffect(() => {
    const refresh = () => force((x) => x + 1);
    window.addEventListener(EV, refresh);
    return () => window.removeEventListener(EV, refresh);
  }, []);

  // Зміна вкладки скидає дату й діапазон — нова вкладка має власну історію.
  const setTab = useCallback((t: MarketTab) => {
    if (state.tab === t) return;
    state.tab = t; state.date = ''; state.min = null; state.max = null; state.period = 'day'; emit();
  }, []);
  /** Оперативні ↔ фактичні ціни БР: та сама дата й діапазон, змінюється лише джерело. */
  const setBrKind = useCallback((t: MarketTab) => {
    if (state.tab !== t && (t === 'br_oper' || t === 'br_fact')) { state.tab = t; emit(); }
  }, []);
  const setPeriod = useCallback((p: MarketPeriod) => { if (state.period !== p) { state.period = p; emit(); } }, []);
  /** Вибір місяця: дата переходить у цей місяць (той самий день, у межах наявних торгів). */
  const setMonth = useCallback((ym: string) => {
    const d = dateInMonth(state.date, ym, state.min, state.max);
    if (state.date !== d) { state.date = d; emit(); }
  }, []);
  const setDate = useCallback((d: string) => { if (state.date !== d) { state.date = d; emit(); } }, []);
  const setView = useCallback((v: MarketView) => { if (state.view !== v) { state.view = v; emit(); } }, []);
  const setRange = useCallback((min: string | null, max: string | null) => {
    if (state.min !== min || state.max !== max) { state.min = min; state.max = max; emit(); }
  }, []);

  return {
    tab: state.tab, date: state.date, view: state.view, period: state.period, min: state.min, max: state.max,
    setTab, setDate, setView, setPeriod, setMonth, setBrKind, setRange,
  };
}
